import { BellRing, FileWarning, MapPinned, School } from "lucide-react";

import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";

const schoolBenefits = [
  {
    title: "Pupil tracking",
    icon: MapPinned,
    body: "Give school teams clearer visibility into where journeys are, when arrivals are expected, and what is happening in real time.",
  },
  {
    title: "Arrival visibility",
    icon: School,
    body: "Support smoother site coordination with better arrival insight for reception teams, SEN staff, and operational contacts.",
  },
  {
    title: "Communication flow",
    icon: BellRing,
    body: "Reduce uncertainty by replacing ad hoc updates with a more structured approach to transport communication.",
  },
  {
    title: "Incident reporting",
    icon: FileWarning,
    body: "Create a clearer path for issue visibility, escalation, and follow-up where transport events need attention.",
  },
];

export const SchoolsPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="For schools"
        title="Transport visibility that helps schools coordinate with clarity."
        description="CareFleet supports schools and SEN coordinators with better awareness of pupil journeys, arrival timing, communication updates, and transport incidents through a service model designed for structure, safeguarding, and clearer operational checkpoints."
        highlights={["Pupil journey clarity", "Arrival visibility", "Communication support", "Incident awareness"]}
        actions={[{ label: "Request Demo", to: "/contact", testId: "schools-request-demo-button" }]}
        baseTestId="schools-hero"
        visual={
          <div className="image-shell">
            <img
              src="https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="Wheelchair accessible vehicle supporting a calm school transport handover"
              className="aspect-[5/4] w-full object-cover object-center"
            />
          </div>
        }
      />

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="School coordination"
            title="Designed to reduce uncertainty around daily arrivals and transport updates."
            description="The CareFleet approach helps education teams spend less time chasing transport information and more time preparing for the pupil’s day through clearer systems, communication pathways, and controlled handovers."
            testId="schools-benefits-heading"
          />
          <div className="grid gap-6 lg:grid-cols-2">
            {schoolBenefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <Reveal key={benefit.title}>
                  <div className="soft-card h-full p-8" data-testid={`schools-benefit-${benefit.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h2 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{benefit.title}</h2>
                    <p className="mt-4 text-base leading-8 text-slate-600">{benefit.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};